"use client";

import React from "react";
import { motion } from "framer-motion";
import { Building2, House as Home, ShieldCheck, Hammer, ArrowRight, CircleCheck as CheckCircle2, FileText, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const residentialHighlights = ["Roof repairs, tiling and chimney stack access", "Single and multi-storey home extensions", "External rendering, cladding and timber repairs", "Brickwork repointing and fascia replacements"];
const commercialHighlights = ["Town-centre commercial units and occupied office parks", "Refurbishment, re-cladding and structural adaptations", "Main contractor and developer sub-contract packages", "Pedestrian protection, pavement gantry and public safety controls"];
const tradeAccessItems = [{
  title: "Roofing Scaffolding",
  description: "Independent scaffolding, handrails and loading bays designed specifically for re-roofing and solar teams.",
  to: "/RoofingScaffoldingPage",
  tag: "Trade Access"
}, {
  title: "Temporary Roofs",
  description: "Full weather-protection tin hats and shrink-wrap canopies for sensitive structural builds and repairs.",
  to: "/TemporaryRoofsPage",
  tag: "Weather Protection"
}, {
  title: "Edge Protection",
  description: "BS EN 13374 compliant guardrails, debris netting and toe boards for flat roofs and steel frames.",
  to: "/EdgeProtectionPage",
  tag: "Site Safety"
}];
const coverageAreas = ["Basingstoke Town Centre (RG21)", "Chineham & Bramley (RG24)", "Kempshott & Beggarwood (RG22)", "Old Basing & Hook (RG24)", "Oakley & Overton (RG23)", "Worting & Viables (RG22/RG23)"];
const nearbyAreas = [{
  name: "Fleet",
  to: "/scaffolding-in-fleet"
}, {
  name: "Andover",
  to: "/scaffolding-in-andover"
}, {
  name: "Winchester",
  to: "/scaffolding-in-winchester"
}];
export default function Coverage() {
  return <section id="basingstokescaffoldingcoverage" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5">
            <MapPin className="size-3 text-primary mr-1" />
            Hampshire Operational Area
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Scaffolding coverage across Basingstoke and North Hampshire
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            From town-centre commercial properties and business parks to residential extensions and roof work across RG21, RG22, RG23, and RG24, BMF Scaffolding provides compliant, prompt access solutions backed by NASC and CHAS standards.
          </p>
        </div>

        {/* Two Main Subsections: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Residential Subsection */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex flex-col">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Home className="size-6" />
                    </div>
                    <Badge variant="secondary">Domestic & Private</Badge>
                  </div>
                  <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                    Residential scaffolding in Basingstoke
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground mt-2">
                    Practical, safe and clean scaffolding for homeowners, local builders and specialist trades across Basingstoke.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <div className="rounded-lg bg-muted/50 p-4 border border-border/60">
                    <p className="text-sm font-medium text-foreground mb-3">Key residential scopes handled:</p>
                    <ul className="space-y-2.5">
                      {residentialHighlights.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Explore detailed services
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/ResidentialScaffoldingPage">
                          Residential Scaffolding
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/ChimneyScaffoldingPage">
                          Chimney Scaffolding
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-border/50">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Fast site surveys available</span>
                  <span className="font-medium text-foreground">Clean handover tags included</span>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Commercial Subsection */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: 0.1
        }} className="flex flex-col">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground">
                      <Building2 className="size-6" />
                    </div>
                    <Badge variant="secondary">Commercial & Development</Badge>
                  </div>
                  <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                    Commercial scaffolding in Basingstoke
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground mt-2">
                    Engineered access for main contractors, commercial property managers, industrial facilities and public sector projects.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <div className="rounded-lg bg-muted/50 p-4 border border-border/60">
                    <p className="text-sm font-medium text-foreground mb-3">Commercial work covered:</p>
                    <ul className="space-y-2.5">
                      {commercialHighlights.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Explore commercial frameworks
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/CommercialScaffoldingPage">
                          Commercial Scaffolding
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/MainContractorsDevelopersPage">
                          Main Contractors & Developers
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-border/50">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>RAMS & TG20:21 design compliance</span>
                  <span className="font-medium text-foreground">Weekly statutory inspections</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2 text-primary font-medium text-sm">
                <Hammer className="size-4" />
                <span>Trade Access Scaffolding</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                Roofing & specialist trade access in Basingstoke
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md mt-2 md:mt-0">
              Reliable access systems tailored for roofing teams, solar installers, and high-level external maintenance contractors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tradeAccessItems.map((item, i) => <motion.div key={item.title} data-index={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            ease: "easeOut",
            delay: i * 0.1
          }}>
                <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs font-normal">
                        {item.tag}
                      </Badge>
                      <ShieldCheck className="size-4 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-2">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-primary/5" asChild>
                      <Link to={item.to}>
                        <span>Learn about {item.title}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Coverage Strip & Direct Action Banner */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 lg:p-12 shadow-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Local Town Coverage
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-secondary-foreground">
                Rapid deployment across all Basingstoke postcodes (RG21–RG24)
              </h3>
              <p className="text-sm sm:text-base text-secondary-foreground/80 leading-relaxed max-w-xl">
                Pete Wood and the BMF operations team coordinate scaffold transport, erection, and strike schedules across Basingstoke&apos;s RG21, RG22, RG23, and RG24 postcode districts with minimal disruption to your site or residents.
              </p>
              
              <div className="pt-2 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {coverageAreas.map((area, i) => <span key={area} data-index={i} className="inline-flex items-center text-xs px-2.5 py-1 rounded-md bg-white/10 text-secondary-foreground font-medium">
                      {area}
                    </span>)}
                </div>

                <div className="pt-3 border-t border-white/10">
                  <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground/70 block mb-2">
                    Nearby areas covered
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    {nearbyAreas.map((area, i) => <Button key={area.name} data-index={i} variant="outline" size="sm" className="h-8 text-xs bg-white/10 hover:bg-white/20 border-white/15 text-white hover:text-white" asChild>
                        <Link to={area.to}>
                          <MapPin className="size-3 text-primary mr-1" />
                          {area.name}
                        </Link>
                      </Button>)}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center lg:items-end">
              <Button size="lg" className="w-full sm:w-auto lg:w-64 bg-primary text-primary-foreground hover:bg-primary/90" asChild>
                <Link to="/ContactRequestQuotePage">
                  <FileText className="size-4 mr-2" />
                  Request a Basingstoke quote
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto lg:w-64 border-border text-foreground bg-background hover:bg-accent" asChild>
                <Link to="#">
                  <MapPin className="size-4 mr-2 text-primary" />
                  View all areas
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}